import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { copyTextToClipboard } from '../../utils/clipboard';
import { formatUsPhoneDisplay } from '../../utils/usPhone';

function normalizePhones(value) {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) {
    return value.filter((phone) => phone != null && String(phone).trim() !== '');
  }
  const text = String(value).trim();
  if (!text) return [];
  if (text.includes(',')) {
    return text.split(',').map((part) => part.trim()).filter(Boolean);
  }
  return [text];
}

function CopyablePhone({ value, className = '', fallback = '—', inline = true }) {
  const { t } = useLanguage();
  const [copiedIndex, setCopiedIndex] = useState(null);
  const phones = useMemo(() => normalizePhones(value), [value]);

  if (phones.length === 0) {
    return <span className={className}>{fallback}</span>;
  }

  const copyPhone = async (phone, index) => {
    const display = formatUsPhoneDisplay(phone) || String(phone).trim();
    try {
      await copyTextToClipboard(display);
      setCopiedIndex(index);
      toast.success(t.common.phoneCopied);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch {
      toast.error(t.common.copyFailed);
    }
  };

  const Wrapper = inline ? 'span' : 'div';

  return (
    <Wrapper className={className}>
      {phones.map((phone, index) => {
        const display = formatUsPhoneDisplay(phone) || String(phone).trim();
        const isCopied = copiedIndex === index;

        return (
          <React.Fragment key={`${display}-${index}`}>
            {index > 0 ? ', ' : null}
            <motion.button
              type="button"
              whileTap={{ scale: 0.97 }}
              onClick={() => copyPhone(phone, index)}
              className={`inline-flex items-center gap-1 rounded-md transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60 ${
                isCopied
                  ? 'text-success-600 '
                  : 'cursor-pointer hover:text-orange-600 '
              }`}
              title={t.common.copyPhone}
              aria-label={`${t.common.copyPhone}: ${display}`}
            >
              <span>{display}</span>
              {isCopied ? <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} /> : null}
            </motion.button>
          </React.Fragment>
        );
      })}
    </Wrapper>
  );
}

export default CopyablePhone;
